import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { tiktokPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import PublishImageUploadZone from '@/tiktok-platform/modules/products/components/PublishImageUploadZone';
import SearchableSelect from '@/tiktok-platform/modules/products/components/SearchableSelect';
import RequiredMark from '@/tiktok-platform/components/RequiredMark';
import {
  TIKTOK_CERTIFICATION_IMAGE_USE_CASE,
  normalizeTiktokComplianceIds,
  resolveCategoryCertifications,
} from '@/tiktok-platform/utils/tiktokCategoryRules';
import {
  focusComplianceEntityField,
  mapTiktokComplianceEntityError,
} from '@/tiktok-platform/utils/mapTiktokComplianceEntityErrors';
import { isTiktokEuMarketRegion } from '@/tiktok-platform/utils/tiktokPublishFormLimits';

/** PFAND additional_attribute 中文标签 */
const PFAND_ATTRIBUTE_OPTIONS = [
  { value: '', label: fixedUi('不填（平台默认）') },
  { value: 'SINGLE_USE', label: fixedUi('一次性包装（SINGLE_USE）') },
  { value: 'REUSABLE', label: fixedUi('可回收包装（REUSABLE）') },
  { value: 'NOT_APPLICABLE', label: fixedUi('不适用（NOT_APPLICABLE）') },
];

function certificationEntry(form, certificationId) {
  const id = String(certificationId || '');
  const found = (Array.isArray(form?.certifications) ? form.certifications : [])
    .find((row) => String(row?.certificationId ?? row?.id) === id);
  return found || { certificationId: id, images: [], files: [], expirationDate: '' };
}

function entityLabel(row, fallback) {
  const profiles = Array.isArray(row?.regional_profiles) && row.regional_profiles.length
    ? row.regional_profiles
    : [row || {}];
  const name = profiles.map((p) => String(p?.name || '').trim()).find(Boolean);
  return `${name || fallback}（${row?.id || ''}）`;
}

/** 制造商 / 欧盟负责人的最小创建表单（对齐 Create Manufacturer / Create Responsible Person 必填项） */
function EuEntityCreateModal({ kind, shopId, onClose, onCreated, onDirtyChange = () => { } }) {
  const isManufacturer = kind === 'manufacturer';
  const bodyRef = useRef(null);
  const [form, setForm] = useState({
    name: '',
    registered_trade_name: '',
    email: '',
    country_code: '+353',
    local_number: '',
    address: '',
    locale: 'en-IE',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const applyErrors = (errors) => {
    setFieldErrors(errors);
    const first = Object.keys(errors)[0];
    if (first) {
      requestAnimationFrame(() => focusComplianceEntityField(bodyRef.current, first));
    }
  };

  const submit = async () => {
    const missing = {};
    if (!form.name.trim()) missing.name = fixedUi('请填写名称');
    if (!form.email.trim()) missing.email = fixedUi('请填写邮箱');
    if (!form.country_code.trim()) missing.country_code = fixedUi('请填写国家码');
    if (!form.local_number.trim()) missing.local_number = fixedUi('请填写本地号码');
    if (!form.address.trim()) missing.address = fixedUi('请填写地址');
    if (Object.keys(missing).length) {
      applyErrors(missing);
      showToast(fixedUi("请填写名称、邮箱、地址与联系电话"), 'warning');
      return;
    }
    if (!shopId) {
      showToast(fixedUi("请先选择店铺"), 'warning');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        registered_trade_name: form.registered_trade_name.trim() || undefined,
        email: form.email.trim(),
        phone_number: {
          availability: 'AVAILABLE',
          country_code: form.country_code.trim(),
          local_number: form.local_number.trim(),
        },
        address: form.address.trim(),
        locale: form.locale.trim() || undefined,
      };
      const res = isManufacturer
        ? await tiktokPlatformAPI.createManufacturer(shopId, payload)
        : await tiktokPlatformAPI.createResponsiblePerson(shopId, payload);
      const id = String(res?.id || res?.data?.id || '').trim();
      showToast(isManufacturer ? fixedUi('制造商已创建') : fixedUi('欧盟负责人已创建'), 'success');
      onDirtyChange(false);
      onCreated?.(id);
      onClose?.();
    } catch (e) {
      const mapped = mapTiktokComplianceEntityError(
        e,
        isManufacturer ? 'manufacturer' : 'responsiblePerson',
      );
      // 本弹窗地址字段统一为 address（负责人完整表单才用 street_address_line1）
      const field = mapped.field === 'street_address_line1' ? 'address' : mapped.field;
      if (field) applyErrors({ [field]: mapped.message });
      showToast(mapped.message || e?.message || fixedUi('创建失败'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const markDirty = () => onDirtyChange(true);

  return (
    <div className="tko-batch-modal" role="dialog" aria-modal="true">
      <button type="button" className="tko-batch-modal__backdrop" aria-label={fixedUi("关闭")} onClick={onClose} />
      <div className="tko-batch-modal__panel">
        <header className="tko-batch-modal__head">
          <h2>{isManufacturer ? fixedUi("新建制造商") : fixedUi("新建欧盟负责人")}</h2>
          <button type="button" className="tko-batch-modal__close" onClick={onClose}>×</button>
        </header>
        <div ref={bodyRef} className="tko-batch-modal__body tko-board-form">
          <label className={`tko-field${fieldErrors.name ? ' tko-field--invalid' : ''}`} data-field-error="name">
            <span className="tko-field__label">{fixedUi("名称")} <RequiredMark /></span>
            <input className="tko-input" value={form.name} disabled={busy} onChange={(e) => { markDirty(); set('name', e.target.value); }} />
            {fieldErrors.name ? <span className="tko-field__error" role="alert">{fieldErrors.name}</span> : null}
          </label>
          <label className={`tko-field${fieldErrors.registered_trade_name ? ' tko-field--invalid' : ''}`} data-field-error="registered_trade_name">
            <span className="tko-field__label">{fixedUi("注册商号")}</span>
            <input className="tko-input" value={form.registered_trade_name} disabled={busy} onChange={(e) => { markDirty(); set('registered_trade_name', e.target.value); }} />
            {fieldErrors.registered_trade_name ? <span className="tko-field__error" role="alert">{fieldErrors.registered_trade_name}</span> : null}
          </label>
          <label className={`tko-field${fieldErrors.email ? ' tko-field--invalid' : ''}`} data-field-error="email">
            <span className="tko-field__label">{fixedUi("邮箱")} <RequiredMark /></span>
            <input className="tko-input" value={form.email} disabled={busy} onChange={(e) => { markDirty(); set('email', e.target.value); }} />
            {fieldErrors.email ? <span className="tko-field__error" role="alert">{fieldErrors.email}</span> : null}
          </label>
          <div className="tko-board-form__row">
            <label className={`tko-field${fieldErrors.country_code ? ' tko-field--invalid' : ''}`} data-field-error="country_code">
              <span className="tko-field__label">{fixedUi("国家码")} <RequiredMark /></span>
              <input className="tko-input" value={form.country_code} disabled={busy} onChange={(e) => { markDirty(); set('country_code', e.target.value); }} />
              {fieldErrors.country_code ? <span className="tko-field__error" role="alert">{fieldErrors.country_code}</span> : null}
            </label>
            <label className={`tko-field${fieldErrors.local_number ? ' tko-field--invalid' : ''}`} data-field-error="local_number">
              <span className="tko-field__label">{fixedUi("本地号码")} <RequiredMark /></span>
              <input className="tko-input" value={form.local_number} disabled={busy} onChange={(e) => { markDirty(); set('local_number', e.target.value); }} />
              {fieldErrors.local_number ? <span className="tko-field__error" role="alert">{fieldErrors.local_number}</span> : null}
            </label>
          </div>
          <label className={`tko-field${fieldErrors.address ? ' tko-field--invalid' : ''}`} data-field-error="address">
            <span className="tko-field__label">{fixedUi("地址")} <RequiredMark /></span>
            <input className="tko-input" value={form.address} disabled={busy} onChange={(e) => { markDirty(); set('address', e.target.value); }} />
            {fieldErrors.address ? <span className="tko-field__error" role="alert">{fieldErrors.address}</span> : null}
          </label>
          <label className={`tko-field${fieldErrors.locale ? ' tko-field--invalid' : ''}`} data-field-error="locale">
            <span className="tko-field__label">{fixedUi("语言档案（locale）")}</span>
            <input className="tko-input" value={form.locale} placeholder={fixedUi("如 en-IE / de-DE")} disabled={busy} onChange={(e) => { markDirty(); set('locale', e.target.value); }} />
            {fieldErrors.locale ? <span className="tko-field__error" role="alert">{fieldErrors.locale}</span> : null}
          </label>
        </div>
        <footer className="tko-batch-modal__foot">
          <button type="button" className="tko-btn" disabled={busy} onClick={onClose}>{fixedUi("取消")}</button>
          <button type="button" className="tko-btn tko-btn--primary" disabled={busy} onClick={() => { void submit(); }}>
            {busy ? fixedUi("提交中…") : fixedUi('创建并选中')}
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * 商品合规区（TK-PUB-25 / TK-PUB-35）：
 * 认证资质 → 合规字段（生产批次号等）→ 制造商/责任人 → COD / PFAND / EPR。
 * 规则来源：Get Category Rules；合规字段由父组件以 extraGroups 传入（归属合规，非商品属性区）。
 * 制造商 / 责任人：欧盟与英国市场展示，且均为选填（UK/GB 同口径）。
 */
export default function TiktokComplianceRulePanel({
  shopId,
  /** 店铺市场二字码（如 DE/FR/GB/US）；用于判定是否展示欧盟/英国合规实体 */
  region = '',
  /**
   * 官方产品模板无国家：强制展示制造商/责任人选填区。
   * 复制到非欧盟店铺时由后端剥离这两项。
   */
  forceShowEuEntities = false,
  rules,
  loading = false,
  error = '',
  value,
  onChange,
  disabled = false,
  onActivate = null,
  /** 平台归入「商品合规」的字段分组（生产批次号、安全警告等，由父组件渲染好传入） */
  extraGroups = null,
}) {
  const form = value || {};
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [manufacturerLoading, setManufacturerLoading] = useState(false);
  const [manufacturerKeyword, setManufacturerKeyword] = useState('');
  const [responsibleOptions, setResponsibleOptions] = useState([]);
  const [responsibleLoading, setResponsibleLoading] = useState(false);
  const [responsibleKeyword, setResponsibleKeyword] = useState('');
  const [createKind, setCreateKind] = useState('');
  const [uploadingCertId, setUploadingCertId] = useState('');

  const activeCertifications = useMemo(
    () => resolveCategoryCertifications(form.productAttributes, rules),
    [form.productAttributes, rules],
  );

  const showEuEntities = forceShowEuEntities || isTiktokEuMarketRegion(region);
  const codSupported = rules?.codSupported === true;
  const pfandRequired = rules?.pfandRequired === true;
  const eprRequired = rules?.eprRequired === true;

  const manufacturerIds = normalizeTiktokComplianceIds(form.manufacturerIds);
  const responsiblePersonIds = normalizeTiktokComplianceIds(form.responsiblePersonIds);

  const loadManufacturers = useCallback(async (keyword = '') => {
    if (!shopId) {
      setManufacturerOptions([]);
      return;
    }
    setManufacturerLoading(true);
    try {
      const res = await tiktokPlatformAPI.searchManufacturers(shopId, {
        pageSize: 50,
        keyword: String(keyword || '').trim() || undefined,
      });
      const data = res?.data && typeof res.data === 'object' ? res.data : res;
      const list = Array.isArray(data?.manufacturers)
        ? data.manufacturers
        : (Array.isArray(data?.items) ? data.items : []);
      setManufacturerOptions(list);
    } catch (e) {
      showToast(e?.message || fixedUi('制造商列表加载失败'), 'error');
    } finally {
      setManufacturerLoading(false);
    }
  }, [shopId]);

  const loadResponsiblePersons = useCallback(async (keyword = '') => {
    if (!shopId) {
      setResponsibleOptions([]);
      return;
    }
    setResponsibleLoading(true);
    try {
      const res = await tiktokPlatformAPI.searchResponsiblePersons(shopId, {
        pageSize: 50,
        keyword: String(keyword || '').trim() || undefined,
      });
      const data = res?.data && typeof res.data === 'object' ? res.data : res;
      const list = Array.isArray(data?.responsible_persons)
        ? data.responsible_persons
        : (Array.isArray(data?.items) ? data.items : []);
      setResponsibleOptions(list);
    } catch (e) {
      showToast(e?.message || fixedUi('负责人列表加载失败'), 'error');
    } finally {
      setResponsibleLoading(false);
    }
  }, [shopId]);

  const prevShowEuRef = useRef(showEuEntities);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    if (showEuEntities) {
      void loadManufacturers('');
      void loadResponsiblePersons('');
    } else if (prevShowEuRef.current) {
      // 从欧盟切到非欧盟：清掉残留，避免误提交
      onChangeRef.current?.({ manufacturerIds: [], responsiblePersonIds: [] });
    }
    prevShowEuRef.current = showEuEntities;
  }, [showEuEntities, loadManufacturers, loadResponsiblePersons]);

  const patchCertification = (certificationId, patch) => {
    const id = String(certificationId);
    const list = Array.isArray(form.certifications) ? form.certifications : [];
    const idx = list.findIndex((row) => String(row?.certificationId ?? row?.id) === id);
    const next = idx >= 0
      ? list.map((row, i) => (i === idx ? { ...row, ...patch } : row))
      : [...list, { certificationId: id, images: [], files: [], expirationDate: '', ...patch }];
    onChange?.({ certifications: next });
  };

  /** 认证 PDF：走平台 Upload Product File（/product/202309/files/upload） */
  const uploadCertificationFile = async (certificationId, file) => {
    if (!shopId) {
      showToast(fixedUi("请先选择店铺"), 'warning');
      return;
    }
    if (!file) return;
    const isPdf = /\.pdf$/i.test(file.name || '') || file.type === 'application/pdf';
    if (!isPdf) {
      showToast(fixedUi("认证文件仅支持 PDF"), 'warning');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      showToast(fixedUi("单个 PDF 不能超过 20MB"), 'warning');
      return;
    }
    const certId = String(certificationId);
    setUploadingCertId(certId);
    try {
      const res = await tiktokPlatformAPI.uploadPublishGoodsFile(shopId, file);
      const payload = res?.data && typeof res.data === 'object' ? res.data : res;
      const id = String(payload?.id || '').trim();
      if (!id) throw new Error(fixedUi('平台未返回文件 id'));
      const entry = certificationEntry(form, certId);
      const files = Array.isArray(entry.files) ? entry.files : [];
      patchCertification(certId, {
        files: [...files, {
          id,
          name: String(payload?.name || file.name || '').trim(),
          format: String(payload?.format || 'PDF').trim(),
        }],
      });
      showToast(fixedUi("认证文件已上传"), 'success');
    } catch (e) {
      showToast(e?.message || fixedUi('认证文件上传失败'), 'error');
    } finally {
      setUploadingCertId('');
    }
  };

  // 规则加载失败或为空、且没有合规字段可填时不渲染整块，避免在无关类目下占用版面
  const nothingToShow = !loading
    && !error
    && !extraGroups
    && !activeCertifications.length
    && !codSupported
    && !pfandRequired
    && !showEuEntities
    && !eprRequired;

  if (nothingToShow) return null;

  return (
    <div
      id="tko-pub-compliance"
      className="tko-wizard__block"
      onFocusCapture={() => { if (typeof onActivate === 'function') onActivate(); }}
    >


      {loading ? <p className="tko-wizard__inline-hint">{fixedUi("正在加载类目规则…")}</p> : null}
      {error ? (
        <p className="tko-wizard__inline-error" role="alert">{error}</p>
      ) : null}

      {activeCertifications.length ? (
        <div className="tko-compliance__group">
          <h4 className="tko-publish__subhead">{fixedUi("认证资质")}</h4>
          <div className="tko-compliance__certs">
            {activeCertifications.map((cert) => {
              const entry = certificationEntry(form, cert.id);
              return (
                <div
                  key={cert.id}
                  className={`tko-compliance__cert${cert.required ? ' is-required' : ''}`}
                  data-field-error="certifications"
                >
                  <div className="tko-compliance__cert-head">
                    <span className="tko-compliance__cert-name">
                      {cert.name}
                      {cert.required ? <RequiredMark /> : <span className="tko-compliance__cert-optional">{fixedUi("（按条件选填）")}</span>}
                    </span>
                    {cert.sampleImageUrl ? (
                      <a
                        className="tko-compliance__cert-sample"
                        href={cert.sampleImageUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {fixedUi('查看样例')}
                      </a>
                    ) : null}
                  </div>
                  {cert.documentDetails ? (
                    <p className="tko-wizard__inline-hint">{cert.documentDetails}</p>
                  ) : null}
                  {cert.conditionMatched ? (
                    <p className="tko-wizard__inline-hint">{fixedUi("已命中属性条件，本条认证当前为必填。")}</p>
                  ) : null}

                  <PublishImageUploadZone
                    label={fixedUi('认证图片')}
                    shopId={shopId}
                    multiple
                    maxCount={6}
                    compact
                    enforceTiktokRules={false}
                    useCase={TIKTOK_CERTIFICATION_IMAGE_USE_CASE}
                    value={entry.images}
                    onChange={(next) => patchCertification(cert.id, { images: Array.isArray(next) ? next : (next ? [next] : []) })}
                    hint=""
                  />

                  {cert.expirationDateRequired ? (
                    <label className="tko-field tko-field--inline">
                      <span className="tko-field__label">{fixedUi("有效期")} <RequiredMark /></span>
                      <input
                        className="tko-input"
                        type="date"
                        value={entry.expirationDate || ''}
                        disabled={disabled}
                        onChange={(e) => patchCertification(cert.id, { expirationDate: e.target.value })}
                      />
                    </label>
                  ) : null}

                  <div className="tko-compliance__cert-files">
                    <div className="tko-compliance__cert-files-head">
                      <span className="tko-field__label">{fixedUi("认证文件（PDF）")}</span>
                      <label className="tko-btn tko-btn--sm">
                        {uploadingCertId === String(cert.id) ? fixedUi('上传中…') : fixedUi('上传 PDF')}
                        <input
                          type="file"
                          accept=".pdf,application/pdf"
                          hidden
                          disabled={disabled || uploadingCertId === String(cert.id)}
                          onChange={(e) => {
                            const picked = e.target.files && e.target.files[0];
                            e.target.value = '';
                            if (picked) void uploadCertificationFile(cert.id, picked);
                          }}
                        />
                      </label>
                    </div>
                    {Array.isArray(entry.files) && entry.files.length ? (
                      <ul className="tko-compliance__cert-file-list">
                        {entry.files.map((file) => (
                          <li key={file.id}>
                            <span className="tko-compliance__cert-file-name">{file.name}</span>
                            <button
                              type="button"
                              className="tko-btn tko-btn--sm"
                              disabled={disabled}
                              onClick={() => patchCertification(cert.id, {
                                files: entry.files.filter((f) => f.id !== file.id),
                              })}
                            >
                              {fixedUi("移除")}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="tko-wizard__inline-hint">{fixedUi("部分认证要求上传 PDF 证明文件（单个 ≤20MB）。")}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="tko-wizard__inline-hint">{fixedUi("认证图片以 use_case=CERTIFICATION_IMAGE 上传；认证 PDF 走平台 Upload Product File（/product/202309/files/upload）。")}</p>
        </div>
      ) : null}

      {/* 合规字段（生产批次号等）在上；制造商 / 责任人紧随其后，同属商品合规而非商品属性 */}
      {extraGroups}

      {showEuEntities ? (
        <div className="tko-compliance__group" data-field-error="manufacturerIds">
          <h4 className="tko-publish__subhead">{fixedUi("制造商")}</h4>
          <p className="tko-wizard__inline-hint">
            {forceShowEuEntities
              ? fixedUi('官方模板选填。可搜索已同步的制造商，或点击新建；复制到欧盟/英国店铺时保留，其它市场会去掉。')
              : fixedUi('欧盟/英国市场选填。可搜索已同步的制造商，或点击新建；组合商品各子品信息不同时请分别维护。')}
          </p>
          <div className="tko-compliance__row">
            <SearchableSelect
              value={manufacturerIds[0] || ''}
              options={manufacturerOptions.map((row) => ({
                value: String(row?.id || ''),
                label: entityLabel(row, fixedUi("制造商")),
              }))}
              loading={manufacturerLoading}
              disabled={disabled || !shopId}
              placeholder={fixedUi("请选择制造商（选填）")}
              emptyText={fixedUi('暂无制造商，请先新建')}
              onSearch={(keyword) => {
                setManufacturerKeyword(keyword);
                void loadManufacturers(keyword);
              }}
              onChange={(next) => onChange?.({ manufacturerIds: next ? [String(next)] : [] })}
            />
            <button
              type="button"
              className="tko-btn tko-btn--sm"
              disabled={disabled || !shopId}
              onClick={() => setCreateKind('manufacturer')}
            >
              {fixedUi("新建制造商")}
            </button>
          </div>
          {manufacturerKeyword ? <p className="tko-wizard__inline-hint">{fixedUiTpl('当前按「{0}」搜索', manufacturerKeyword)}</p> : null}
        </div>
      ) : null}

      {showEuEntities ? (
        <div className="tko-compliance__group" data-field-error="responsiblePersonIds">
          <h4 className="tko-publish__subhead">{fixedUi("添加责任人")}</h4>
          <p className="tko-wizard__inline-hint">
            {forceShowEuEntities
              ? fixedUi('官方模板选填（Responsible Person）。可搜索已同步的责任人，或点击新建；复制到欧盟/英国店铺时保留，其它市场会去掉。')
              : fixedUi('欧盟/英国市场选填（Responsible Person）。可搜索已同步的责任人，或点击新建。')}
          </p>
          <div className="tko-compliance__row">
            <SearchableSelect
              value={responsiblePersonIds[0] || ''}
              options={responsibleOptions.map((row) => ({
                value: String(row?.id || ''),
                label: entityLabel(row, fixedUi('责任人')),
              }))}
              loading={responsibleLoading}
              disabled={disabled || !shopId}
              placeholder={fixedUi("请选择责任人（选填）")}
              emptyText={fixedUi('暂无责任人，请先新建')}
              onSearch={(keyword) => {
                setResponsibleKeyword(keyword);
                void loadResponsiblePersons(keyword);
              }}
              onChange={(next) => onChange?.({ responsiblePersonIds: next ? [String(next)] : [] })}
            />
            <button
              type="button"
              className="tko-btn tko-btn--sm"
              disabled={disabled || !shopId}
              onClick={() => setCreateKind('responsiblePerson')}
            >
              {fixedUi('新建责任人')}
            </button>
          </div>
          {responsibleKeyword ? <p className="tko-wizard__inline-hint">{fixedUiTpl('当前按「{0}」搜索', responsibleKeyword)}</p> : null}
        </div>
      ) : null}

      {codSupported ? (
        <div className="tko-compliance__group">
          <h4 className="tko-publish__subhead">{fixedUi("货到付款（COD）")}</h4>
          <label className="tko-field tko-field--inline">
            <span className="tko-field__label">{fixedUi("允许 COD")}</span>
            <select
              className="tko-input"
              value={form.isCodAllowed === true ? '1' : (form.isCodAllowed === false ? '0' : '')}
              disabled={disabled}
              onChange={(e) => {
                const raw = e.target.value;
                onChange?.({ isCodAllowed: raw === '' ? null : raw === '1' });
              }}
            >
              <option value="">{fixedUi("不设置（平台默认）")}</option>
              <option value="1">{fixedUi("支持 COD")}</option>
              <option value="0">{fixedUi("不支持 COD")}</option>
            </select>
          </label>
          <p className="tko-wizard__inline-hint">{fixedUi("本类目支持 COD；若设为「不支持」，买家结算页不展示货到付款。")}</p>
        </div>
      ) : null}

      {pfandRequired ? (
        <div className="tko-compliance__group" data-field-error="pfandAmount">
          <h4 className="tko-publish__subhead">{fixedUi("押金（PFAND）")}</h4>
          <div className="tko-compliance__row">
            <label className="tko-field tko-field--inline">
              <span className="tko-field__label">{fixedUi("押金金额")} <RequiredMark /></span>
              <input
                className="tko-input"
                inputMode="decimal"
                value={form.pfandAmount || ''}
                disabled={disabled}
                placeholder={fixedUi("如 0.25")}
                onChange={(e) => onChange?.({ pfandAmount: e.target.value })}
              />
            </label>
            <label className="tko-field tko-field--inline">
              <span className="tko-field__label">{fixedUi("包装属性")}</span>
              <select
                className="tko-input"
                value={String(form.pfandType || '')}
                disabled={disabled}
                onChange={(e) => onChange?.({ pfandType: e.target.value })}
              >
                {PFAND_ATTRIBUTE_OPTIONS.map((opt) => (
                  <option key={opt.value || 'none'} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
          </div>
          <p className="tko-wizard__inline-hint">{fixedUi('押金按 SKU 提交（`skus[].fees`），当前金额会应用到全部 SKU。取值范围 0.00–6300.00。')}</p>
        </div>
      ) : null}

      {eprRequired ? (
        <div className="tko-compliance__group">
          <h4 className="tko-publish__subhead">{fixedUi("生产者责任延伸（EPR）")}</h4>
          <p className="tko-wizard__inline-hint">
            {fixedUi('本类目要求 EPR 信息。Create Product 无独立 EPR 字段，请在上方「商品合规」中填写平台下发的相关项（如 EPR 注册号）。')}
          </p>
        </div>
      ) : null}

      {createKind ? (
        <EuEntityCreateModal
          kind={createKind}
          shopId={shopId}
          onClose={() => setCreateKind('')}
          onCreated={(id) => {
            if (createKind === 'manufacturer') {
              void loadManufacturers('');
              onChange?.({ manufacturerIds: id ? [String(id)] : manufacturerIds });
            } else {
              void loadResponsiblePersons('');
              onChange?.({ responsiblePersonIds: id ? [String(id)] : responsiblePersonIds });
            }
          }}
        />
      ) : null}
    </div>
  );
}
